import {PageHeader} from '../ui/PageHeader';
import {DataTable} from '../ui/DataTable';
import {StatusBadge} from '../ui/StatusBadge';
import { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { engineWorkspaces, operatingServices } from '@myfm/domain';
import { api } from '../api';
import { DateOnlyInput } from '../components/DateOnlyInput';
import { formatDateOnly } from '../dates';
import { Empty, ErrorState, Loading } from '../ui/State';
import './engine-workspaces.css';
type Source = { kind: string; id: string; condition: string };
type Contribution = {
  id: string;
  engine: string;
  service: string;
  brief: string;
  criterion: string;
  required: boolean;
  targetOn: string | null;
  source: Source | null;
  residualOwner: string | null;
  residualDueOn: string | null;
};
type Plan = {
  title: string;
  purpose: string;
  scope: string;
  acceptance: string;
  contributions: Contribution[];
  edges: { from: string; to: string; kind: string; condition: string; nextParty: string }[];
  commitments: any[];
};
type Detail = {
  id: string;
  version: number;
  lifecycle: string;
  health: string;
  timing: string;
  definition: Plan;
  canCoordinate: boolean;
  engineActions: string[];
  ownership: string;
  contributionOwnership: Record<string, string>;
  responses: Record<string, { response: string; note: string; targetOn: string | null }>;
  sources: Record<string, { coverage: string; state?: string; href?: string }>;
  proofs: { id: string; accepted: boolean; current: boolean; satisfied: boolean }[];
  history: { version: number; command: string; basis: string; created_at: string; promises: { id: string; promise: string; dueOn: string | null }[] }[];
};
const fresh = (): Contribution => ({
  id: crypto.randomUUID(),
  engine: 'repairs',
  service: 'S-RM-01',
  brief: '',
  criterion: '',
  required: true,
  targetOn: null,
  source: null,
  residualOwner: null,
  residualDueOn: null
});
export function BuildingOutcomes() {
  const { id: building, outcomeId } = useParams();
  const [draftId] = useState(() => crypto.randomUUID()),
    requestKeys = useRef(new Map<string, string>());
  const [editing, setEditing] = useState(false),
    [detail, setDetail] = useState<Detail>(),
    [list, setList] = useState<{ items: { id: string; title: string; health: string }[]; hasMore: boolean; canCreate: boolean }>(),
    [offset, setOffset] = useState(0),
    [refresh, setRefresh] = useState(0),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState<Plan>({ title: '', purpose: '', scope: 'Physical repair', acceptance: '', contributions: [], edges: [], commitments: [] }),
    [contribution, setContribution] = useState(fresh),
    [sources, setSources] = useState<{ id: string; kind: string; condition: string; state: string; label: string; ownerId?: string | null; dueOn?: string | null }[]>([]),
    [sourcePage, setSourcePage] = useState(0),
    [hasMore, setHasMore] = useState(false),
    [reason, setReason] = useState(''),
    [note, setNote] = useState(''),
    [target, setTarget] = useState('');
  const [actions, setActions] = useState<{ id: string; state: string; label: string }[]>([]),
    [promise, setPromise] = useState({ id: crypto.randomUUID(), promise: '', recipientReference: '', dueOn: null as string | null, instructionId: '', fulfilmentId: '' });
  const [linking, setLinking] = useState<string>(),
    [linkedSourceId, setLinkedSourceId] = useState(''),
    [linkedCondition, setLinkedCondition] = useState('Invoice approved');
  const [sourceKind, setSourceKind] = useState('Work'),
    [edge, setEdge] = useState({ from: '', to: '', kind: 'Blocks', condition: '', nextParty: '' });
  useEffect(() => {
    const c = new AbortController();
    setError(undefined);
    void api<any>(outcomeId ? '/api/building-outcomes/' + outcomeId + '?buildingId=' + building : '/api/building-outcomes?buildingId=' + building + '&offset=' + offset, {
      signal: c.signal
    })
      .then((data) => {
        if (c.signal.aborted) return;
        if (outcomeId) {
          setDetail(data);
          setPlan(data.definition);
        } else setList(data);
      })
      .catch((e) => {
        if (!c.signal.aborted) setError(e);
      });
    return () => c.abort();
  }, [building, outcomeId, offset, refresh]);
  useEffect(() => {
    const c = new AbortController();
    setSources([]);
    void api<any>('/api/building-outcome-sources?buildingId=' + building + '&kind=' + sourceKind + '&offset=' + sourcePage, { signal: c.signal })
      .then((data) => {
        if (!c.signal.aborted) {
          setSources(data.items);
          setHasMore(data.hasMore);
        }
      })
      .catch((e) => {
        if (!c.signal.aborted) setError(e);
      });
    return () => c.abort();
  }, [building, sourceKind, sourcePage, refresh]);
  useEffect(() => {
    const controller = new AbortController();
    void api<any>('/api/building-outcome-sources?buildingId=' + building + '&kind=Action', { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setActions(data.items);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [building, refresh]);
  async function command(command: string, extra: Record<string, unknown> = {}) {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const payload = { id: detail?.id ?? draftId, buildingId: building, expectedVersion: detail?.version ?? 0, basis: reason, command, ...extra },
        identity = JSON.stringify(payload);
      let key = requestKeys.current.get(identity);
      if (!key) {
        key = crypto.randomUUID();
        requestKeys.current.set(identity, key);
      }
      const result = await api<{ id: string }>('/api/building-outcomes/commands', { method: 'POST', body: JSON.stringify({ ...payload, idempotencyKey: key }) });
      if (!outcomeId) window.location.assign('/buildings/' + building + '/outcomes/' + result.id + '?building=' + building);
      else {
        setEditing(false);
        setLinking(undefined);
        setRefresh((r) => r + 1);
      }
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const editable = detail ? detail.canCoordinate && ['Draft', 'Active'].includes(detail.lifecycle) : list?.canCreate;
  const contributionForm = (
    <fieldset disabled={busy}>
      <legend>Engine contribution</legend>
      <label>
        Engine{' '}
        <select
          value={contribution.engine}
          onChange={(e) => {
            const engine = e.target.value;
            setContribution({ ...contribution, engine, service: operatingServices[engine]![0]!, source: null });
            setSourceKind(engine === 'repairs' ? 'Work' : engine === 'governance' ? 'Governance' : engine === 'finance' ? 'Invoice' : 'Action');
            setSourcePage(0);
          }}
        >
          {engineWorkspaces.map((e) => (
            <option key={e.code} value={e.code}>
              {e.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Handoff brief <input value={contribution.brief} onChange={(e) => setContribution({ ...contribution, brief: e.target.value })} />
      </label>
      <label>
        Contribution acceptance criterion <input value={contribution.criterion} onChange={(e) => setContribution({ ...contribution, criterion: e.target.value })} />
      </label>
      <label>
        Contribution target <DateOnlyInput value={contribution.targetOn ?? ''} onChange={(v) => setContribution({ ...contribution, targetOn: v || null })} />
      </label>
      <label>
        Original record type{' '}
        <select
          value={sourceKind}
          onChange={(e) => {
            setSourceKind(e.target.value);
            setSourcePage(0);
            setContribution({ ...contribution, source: null });
          }}
        >
          <option>Work</option>
          <option>Action</option>
          <option>Governance</option>
          <option>Invoice</option>
        </select>
      </label>
      <label>
        Original record{' '}
        <select
          value={contribution.source?.id ?? ''}
          onChange={(e) => {
            const s = sources.find((s) => s.id === e.target.value);
            setContribution({
              ...contribution,
              source: s ? { id: s.id, kind: sourceKind, condition: s.condition } : null,
              ...(!contribution.required ? { residualOwner: s?.ownerId ?? null, residualDueOn: s?.dueOn ?? null } : {})
            });
          }}
        >
          <option value="">Awaiting original work</option>
          {sources.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label} · {s.state}
            </option>
          ))}
        </select>
      </label>
      {sourcePage > 0 && (
        <button type="button" onClick={() => setSourcePage((p) => Math.max(0, p - 50))}>
          Previous records
        </button>
      )}
      {hasMore && (
        <button type="button" onClick={() => setSourcePage((p) => p + 50)}>
          More original records
        </button>
      )}
      {contribution.source?.kind === 'Invoice' && (
        <label>
          Required financial milestone{' '}
          <select value={contribution.source.condition} onChange={(e) => setContribution({ ...contribution, source: { ...contribution.source!, condition: e.target.value } })}>
            <option>Invoice approved</option>
            <option>Invoice paid</option>
          </select>
        </label>
      )}
      <label>
        <input
          type="checkbox"
          style={{ width: 'auto' }}
          checked={contribution.required}
          onChange={(e) => {
            const s = sources.find((s) => s.id === contribution.source?.id);
            setContribution({
              ...contribution,
              required: e.target.checked,
              residualOwner: e.target.checked ? null : (s?.ownerId ?? null),
              residualDueOn: e.target.checked ? null : (s?.dueOn ?? null)
            });
          }}
        />{' '}
        Required for this outcome
      </label>
      {!contribution.required && (
        <p>
          {contribution.source?.kind === 'Action' && contribution.residualOwner && contribution.residualDueOn
            ? 'Original action owner retained; due ' + formatDateOnly(contribution.residualDueOn)
            : 'Choose an original action with a recorded owner and due date for work outside this outcome.'}
        </p>
      )}
      <button
        type="button"
        disabled={
          (!contribution.required && (!contribution.residualOwner || !contribution.residualDueOn || contribution.source?.kind !== 'Action')) ||
          contribution.brief.trim().length < 3 ||
          contribution.criterion.trim().length < 3
        }
        onClick={() => {
          setPlan({
            ...plan,
            contributions: plan.contributions.some((c) => c.id === contribution.id)
              ? plan.contributions.map((c) => (c.id === contribution.id ? contribution : c))
              : [...plan.contributions, contribution]
          });
          setContribution(fresh());
        }}
      >
        Add contribution to plan
      </button>
    </fieldset>
  );
  return (
    <div className="engine-workspaces building-outcomes">
      <PageHeader title={detail?.definition.title ?? 'Building outcomes'}
        context="Coordinate an agreed result and review the original work, decisions and evidence."
        secondaryActions={[{kind:'link',label:detail?'All building outcomes':'Building overview',to:detail?'/buildings/'+building+'/outcomes?building='+building:'/buildings/'+building+'?building='+building}]}/>
      {error !== undefined && <ErrorState error={error} />}
      {busy && <p role="status">Saving coordination…</p>}
      {!detail && !list && error === undefined && <Loading />}
      {list && (
        <section aria-label="Building outcomes">
          {list.items.length?<DataTable caption={<span className="sr-only">Building outcomes</span>} scrollLabel="Building outcome records"
            rows={list.items} getRowKey={item=>item.id}
            rowHeader={{id:'outcome',header:'Agreed result',render:item=><Link to={'/buildings/'+building+'/outcomes/'+item.id+'?building='+building}>{item.title}</Link>}}
            columns={[{id:'health',header:'Recorded position',render:item=><StatusBadge text={item.health}/>}]}/>:
            <Empty>No building outcomes are recorded in this view.{list.canCreate?' Create a plan below when a result needs coordination across the original work records.':' The Property Manager can coordinate an agreed result when one is needed.'}</Empty>}
          {offset > 0 && <button onClick={() => setOffset((o) => Math.max(0, o - 20))}>Previous outcomes</button>}
          {list.hasMore && <button onClick={() => setOffset((o) => o + 20)}>More outcomes</button>}
        </section>
      )}
      {detail && (
        <>
          <p role="status">
            {detail.health} · {detail.ownership} · Revision {detail.version}
          </p>
          <p>{detail.definition.purpose}</p>
          <p>
            Agreed result: {detail.definition.scope}. {detail.definition.acceptance}
          </p>
          <section aria-label="Engine contributions">
            <h2>Engine contributions</h2>
            {detail.definition.contributions.map((c) => {
              const proof = detail.proofs.find((p) => p.id === c.id),
                source = detail.sources[c.id];
              return (
                <article key={c.id}>
                  <h3>
                    {engineWorkspaces.find((e) => e.code === c.engine)?.name}: {c.brief}
                  </h3>
                  <p>Engine responsibility: {detail.contributionOwnership[c.id] ?? 'Unconfirmed'}</p>
                  <p>
                    {c.criterion} · {c.required ? 'Required' : 'Retained residual work'} · {c.targetOn ? formatDateOnly(c.targetOn) : 'Target unconfirmed'}
                  </p>
                  <p>
                    {detail.responses[c.id]?.response ?? 'Awaiting engine response'} · {source?.coverage ?? 'Unsupported'} ·{' '}
                    {proof?.current && proof.satisfied ? 'Current criterion satisfied' : 'Evidence needs review'}
                  </p>
                  {source?.href && <Link to={source.href}>Open original record</Link>}
                  {detail.responses[c.id] && (
                    <p>
                      {detail.responses[c.id]?.note} · Engine commitment:{' '}
                      {detail.responses[c.id]?.targetOn ? formatDateOnly(detail.responses[c.id]!.targetOn!) : 'Date unconfirmed'}
                    </p>
                  )}
                  {!c.source && detail.lifecycle === 'Active' && (detail.canCoordinate || detail.engineActions.includes(c.id)) && (
                    <button
                      onClick={() => {
                        setLinking(c.id);
                        setLinkedSourceId('');
                        setLinkedCondition(detail.definition.scope === 'Full project closeout' ? 'Invoice paid' : 'Invoice approved');
                        setSourceKind(c.engine === 'repairs' ? 'Work' : c.engine === 'governance' ? 'Governance' : c.engine === 'finance' ? 'Invoice' : 'Action');
                        setSourcePage(0);
                      }}
                    >
                      Choose original work
                    </button>
                  )}
                  {detail.engineActions.includes(c.id) && detail.lifecycle === 'Active' && (
                    <div>
                      {(['Accepted', 'Queried', 'Returned'] as const).map((response) => (
                        <button
                          key={response}
                          disabled={busy || reason.trim().length < 3 || note.trim().length < 3}
                          onClick={() => void command('RespondHandoff', { contributionId: c.id, response, responseNote: note, targetOn: target || null })}
                        >
                          {response === 'Accepted' ? 'Accept or refresh evidence' : response === 'Queried' ? 'Query handoff' : 'Return handoff'}
                        </button>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </section>
          {linking && (
            <fieldset>
              <legend>Link original work</legend>
              <label>
                Linked record type{' '}
                <select
                  value={sourceKind}
                  onChange={(e) => {
                    setSourceKind(e.target.value);
                    setSourcePage(0);
                    setLinkedSourceId('');
                  }}
                >
                  <option>Work</option>
                  <option>Action</option>
                  <option>Governance</option>
                  <option>Invoice</option>
                </select>
              </label>
              <label>
                Record for this contribution{' '}
                <select value={linkedSourceId} onChange={(e) => setLinkedSourceId(e.target.value)}>
                  <option value="">Choose original work</option>
                  {sources.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label} · {s.state}
                    </option>
                  ))}
                </select>
              </label>
              {sourceKind === 'Invoice' && (
                <label>
                  Linked financial milestone{' '}
                  <select value={linkedCondition} onChange={(e) => setLinkedCondition(e.target.value)}>
                    <option>Invoice approved</option>
                    <option>Invoice paid</option>
                  </select>
                </label>
              )}
              {hasMore && <button onClick={() => setSourcePage((p) => p + 50)}>More matching records</button>}
              <button
                disabled={busy || !linkedSourceId || reason.trim().length < 3}
                onClick={() => {
                  const source = sources.find((s) => s.id === linkedSourceId);
                  if (source)
                    void command('LinkSource', {
                      contributionId: linking,
                      source: { kind: sourceKind, id: source.id, condition: sourceKind === 'Invoice' ? linkedCondition : source.condition }
                    });
                }}
              >
                Link original record
              </button>
            </fieldset>
          )}
          {detail.engineActions.length > 0 && (
            <fieldset>
              <legend>Engine response</legend>
              <label>
                Response note <input value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
              <label>
                Engine commitment date <DateOnlyInput value={target} onChange={setTarget} />
              </label>
            </fieldset>
          )}
          <p>Outstanding commitment dates: {detail.timing}</p>
          <h2>Dependencies</h2>
          <ul>
            {detail.definition.edges.map((e, i) => (
              <li key={i}>
                {detail.definition.contributions.find((c) => c.id === e.from)?.brief} → {detail.definition.contributions.find((c) => c.id === e.to)?.brief} · {e.kind} ·{' '}
                {e.condition} · Next: {e.nextParty}
              </li>
            ))}
          </ul>
          <h2>History</h2>
          <ol>
            {detail.history.map((h) => (
              <li key={h.version}>
                Revision {h.version} ·{' '}
                {(
                  {
                    Create: 'Plan created',
                    Amend: 'Plan amended',
                    Activate: 'Handoffs requested',
                    RespondHandoff: 'Engine response',
                    Close: 'Building result accepted',
                    Reopen: 'Coordination reopened',
                    Cancel: 'Coordination cancelled',
                    ChangeCoordinationOwner: 'Coordination owner changed',
                    LinkSource: 'Original work linked'
                  } as Record<string, string>
                )[h.command] ?? h.command}{' '}
                · {h.basis}
                {h.promises.map((p) => (
                  <p key={p.id}>
                    Recorded promise: {p.promise} · {p.dueOn ? formatDateOnly(p.dueOn) : 'Date unconfirmed'}
                  </p>
                ))}
              </li>
            ))}
          </ol>
        </>
      )}
      {detail && editable && !editing && <button onClick={() => setEditing(true)}>Edit outcome plan</button>}
      {editable && (!detail || editing) && (
        <section aria-label="Outcome plan">
          <h2>{detail ? 'Amend the plan' : 'Create an outcome'}</h2>
          <label>
            Outcome title <input value={plan.title} onChange={(e) => setPlan({ ...plan, title: e.target.value })} />
          </label>
          <label>
            Intended result <input value={plan.purpose} onChange={(e) => setPlan({ ...plan, purpose: e.target.value })} />
          </label>
          <label>
            Agreed scope{' '}
            <select value={plan.scope} disabled={Boolean(detail)} onChange={(e) => setPlan({ ...plan, scope: e.target.value })}>
              <option>Physical repair</option>
              <option>Full project closeout</option>
              <option>Inspection findings</option>
            </select>
          </label>
          <label>
            Outcome acceptance criteria <input value={plan.acceptance} onChange={(e) => setPlan({ ...plan, acceptance: e.target.value })} />
          </label>
          <ul>
            {plan.contributions.map((c) => (
              <li key={c.id}>
                {c.brief} · {c.engine} · {c.source?.condition ?? 'Awaiting original work'}{' '}
                <button
                  onClick={() => {
                    setContribution(c);
                    setSourceKind(c.source?.kind ?? 'Action');
                    setSourcePage(0);
                  }}
                >
                  Edit contribution
                </button>
              </li>
            ))}
          </ul>
          {contributionForm}
          {plan.contributions.length > 1 && (
            <fieldset>
              <legend>Add a coordination dependency</legend>
              {(['from', 'to'] as const).map((key) => (
                <label key={key}>
                  {key === 'from' ? 'Prerequisite contribution' : 'Waiting contribution'}{' '}
                  <select value={edge[key]} onChange={(e) => setEdge({ ...edge, [key]: e.target.value })}>
                    <option value="">Choose contribution</option>
                    {plan.contributions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.brief}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
              <label>
                Dependency kind{' '}
                <select value={edge.kind} onChange={(e) => setEdge({ ...edge, kind: e.target.value })}>
                  <option>Blocks</option>
                  <option>Related</option>
                </select>
              </label>
              <label>
                Expected condition <input value={edge.condition} onChange={(e) => setEdge({ ...edge, condition: e.target.value })} />
              </label>
              <label>
                Next responsible party <input value={edge.nextParty} onChange={(e) => setEdge({ ...edge, nextParty: e.target.value })} />
              </label>
              <button
                disabled={!edge.from || !edge.to || edge.condition.length < 3 || edge.nextParty.length < 3}
                onClick={() => setPlan({ ...plan, edges: [...plan.edges, edge] })}
              >
                Add dependency to plan
              </button>
            </fieldset>
          )}
          <fieldset>
            <legend>Stakeholder commitment</legend>
            <label>
              Promised result <input value={promise.promise} onChange={(e) => setPromise({ ...promise, promise: e.target.value })} />
            </label>
            <label>
              Recipient or contact reference <input value={promise.recipientReference} onChange={(e) => setPromise({ ...promise, recipientReference: e.target.value })} />
            </label>
            <label>
              Promised date <DateOnlyInput value={promise.dueOn ?? ''} onChange={(v) => setPromise({ ...promise, dueOn: v || null })} />
            </label>
            {(['instructionId', 'fulfilmentId'] as const).map((key) => (
              <label key={key}>
                {key === 'instructionId' ? 'Original instruction action' : 'Original fulfilment action'}{' '}
                <select value={promise[key]} onChange={(e) => setPromise({ ...promise, [key]: e.target.value })}>
                  <option value="">Choose original action</option>
                  {actions.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label} · {a.state}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <button
              disabled={promise.promise.length < 3 || promise.recipientReference.length < 3 || !promise.instructionId || !promise.fulfilmentId}
              onClick={() => {
                const { instructionId, fulfilmentId, ...c } = promise;
                setPlan({
                  ...plan,
                  commitments: [
                    ...plan.commitments,
                    {
                      ...c,
                      instruction: { kind: 'Action', id: instructionId, condition: 'Action verified' },
                      fulfilment: { kind: 'Action', id: fulfilmentId, condition: 'Action verified' }
                    }
                  ]
                });
                setPromise({ id: crypto.randomUUID(), promise: '', recipientReference: '', dueOn: null, instructionId: '', fulfilmentId: '' });
              }}
            >
              Add commitment to plan
            </button>
          </fieldset>
          <ul>
            {plan.commitments.map((c) => (
              <li key={c.id}>
                {c.promise} · {c.recipientReference} · {c.dueOn ? formatDateOnly(c.dueOn) : 'Promised date unconfirmed'}
              </li>
            ))}
          </ul>
          <button disabled={busy || reason.trim().length < 3 || !plan.contributions.length} onClick={() => void command(detail ? 'Amend' : 'Create', { definition: plan })}>
            Save outcome plan
          </button>
        </section>
      )}
      {(editable || detail?.canCoordinate || Boolean(detail?.engineActions.length)) && (
        <label>
          Reason for this coordination decision <input value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      )}
      {detail?.canCoordinate && (
        <section aria-label="Outcome decisions">
          <h2>Outcome decisions</h2>
          {(detail.lifecycle === 'Draft'
            ? ['Activate', 'Cancel']
            : detail.lifecycle === 'Active'
              ? ['Close', 'ChangeCoordinationOwner', 'Cancel']
              : detail.lifecycle === 'Closed'
                ? ['Reopen', 'Cancel']
                : []
          ).map((c) => (
            <button key={c} disabled={busy || reason.trim().length < 3 || (c === 'Close' && detail.health !== 'Ready for outcome review')} onClick={() => void command(c)}>
              {
                (
                  {
                    Activate: 'Request engine contributions',
                    Close: 'Accept building result',
                    Reopen: 'Reopen coordination',
                    Cancel: 'Cancel coordination',
                    ChangeCoordinationOwner: 'Take coordination ownership'
                  } as Record<string, string>
                )[c]
              }
            </button>
          ))}
        </section>
      )}
    </div>
  );
}
